import { Box, ButtonBase, CircularProgress, InputBase, Skeleton, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import cx from 'classnames';
import { SettingsOutlined } from "@mui/icons-material";
import { calculateDaysBetween, chartDateOptions, chartDateTimeFrames, chartTypes, normalizeFrame, rhChartColorsDark, rhChartColorsLight } from '../chartFilterEnums';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../../theme';
import { useEffect, useMemo, useRef, useState } from 'react';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ShDateRangePicker from '../../../theme/ShDateRangePicker';
import { useReportHubFilters } from '../ReportHubFilterContext';
import { AreaChartContentReports } from '../../../theme/charts/AreaChart';
import { RHBarChart } from '../charts/RHBarChart';
import { LineChartContentReports } from '../../../theme/charts/LineChart';
import { ReportDetailsPageDetails } from './ReportDetailsPageDetails';
import { MRhSettings } from './MRhSettings';
import { RhTimeframeDropDown } from './RhTimeframe';
import { RhDateSelector } from './RhDateSelector';
import { RhSubtypeToggle } from './RhSubtypeToggle';
import { RhRollingCompare, compareLabel } from './RhRollingCompare';
import Text from '../../../theme/text/Text';
import { getReportForEnvironment, getReportKey } from '../reports';
import NumberInput from '../../../theme/inputs/NumberInput';
import { MRhFilter } from './MRhFilter';
import { RhFilter } from './RhFilter';
import EditIcon from '@mui/icons-material/Edit';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Heading from '../../../theme/text/Heading';
import { ErrorBoundary } from 'react-error-boundary';
export const RhChart = (props) => {
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const filtersRegistry = useReportHubFilters();

    const chartOption = getReportForEnvironment(getReportKey(props.currentReport), filtersRegistry.environment);

    const categorySettings = useMemo(() => {
        return filtersRegistry?.chartSettings?.categorySettings?.();
    }, [filtersRegistry]);

    const [edittingName,setEdittingName] = useState(false);
    useEffect(() => {
        setEdittingName(props.defaultNameEdit);
    },[props.defaultNameEdit])

    const [anchorEl, setAnchorEl] = useState(null);
    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'popover' : undefined;
    
    const handlePopoverClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [rhFilterModalOpen, setRhFilterModalOpen] = useState(false);
    const rhFilterModal = {
        open: rhFilterModalOpen,
        setOpen: setRhFilterModalOpen
    }

    const handleRhFilterClose = () => {
        setRhFilterModalOpen(false);
    };



    const [rhSettingsModalOpen, setRhSettingsModalOpen] = useState(false);
    const rhSettingsModal = {
        open: rhSettingsModalOpen,
        setOpen: setRhSettingsModalOpen
    }

    const handleRhSettingsClose = () => {
        setRhSettingsModalOpen(false);
    };

    // Track if chart data has been loaded at least once
    const [chartDataLoaded, setChartDataLoaded] = useState(false);
    // Track if chart has finished rendering (with delay to let ApexCharts paint)
    const [chartRendered, setChartRendered] = useState(false);
    const prevChartInfoRef = useRef(props.chartInfo);

    // When comparison periods are chosen the backend returns the current period plus one position-aligned
    // line per comparison; overlay them instead of the raw series (mirrors the pivot-detail chart). Available
    // for every time-based date type (relative / dynamic / rolling), not category charts.
    const rolling = props.chartInfo?.rolling;
    const hasCompareOverlay = !!rolling;
    const showCompareBar = !props.currentReport?.category?.use;
    const chartValues = useMemo(() => {
        if (!hasCompareOverlay) return props.chartInfo?.series;
        const specs = props.currentReport?.date?.compare ?? [];
        const comparisons = rolling.series.comparisons ?? [];
        // The backend groups comparison lines by spec/recurrence, which interleaves years in the legend
        // (e.g. all of 2025's months before 2024's). Order them by their actual period — most recent first —
        // so the legend steps cleanly back in time from the current period. Lines are position-aligned, not
        // calendar-aligned, so reordering only affects the legend, not the overlay.
        const ts = (cmp) => { const d = new Date(cmp?.start); return isNaN(d) ? 0 : d.getTime(); };
        const ordered = [...comparisons].sort((a, b) => ts(b) - ts(a));
        const out = [{ name: t('this_period'), data: rolling.series.current }];
        ordered.forEach((cmp) => {
            out.push({ name: compareLabel(specs[cmp.specIndex], cmp, t, i18n.language), data: cmp.data });
        });
        return out;
    }, [hasCompareOverlay, rolling, props.chartInfo, props.currentReport, t, i18n.language]);
    const chartCategories = hasCompareOverlay ? rolling.labels : props.chartInfo?.categories;

    // An `all` category stores no values of its own — the backend resolves the list and returns it as
    // the chart's categories — so that response is what the selection summary can count there.
    let categorySelectionCount = props.currentReport?.category?.values?.length ?? 0;
    if (props.currentReport?.category?.all) {
        categorySelectionCount = props.chartInfo?.categories?.length ?? 0;
    }

    // Set chartDataLoaded to true once we have chart data
    useEffect(() => {
        if (props.chartInfo && !props.isLoading && !props.fetchFetching) {
            setChartDataLoaded(true);
        }
    }, [props.chartInfo, props.isLoading, props.fetchFetching]);

    // After data is loaded, wait for chart to render before showing it
    useEffect(() => {
        if (chartDataLoaded && !chartRendered) {
            // Give the chart time to render (ApexCharts needs time to paint)
            const timeoutId = setTimeout(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setChartRendered(true);
                    });
                });
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [chartDataLoaded, chartRendered]);

    // Reset states when chart type changes (new report)
    useEffect(() => {
        if (props.currentReport?.type !== prevChartInfoRef.current?.type) {
            setChartDataLoaded(false);
            setChartRendered(false);
        }
        prevChartInfoRef.current = props.currentReport;
    }, [props.currentReport?.type]);

    // Show skeleton until chart has fully rendered
    const showSkeleton = !chartRendered;

    return (
        <Box 
            className={cx(undefined, {
                'mobile-line':props.currentReport?.displayType === chartTypes.LINE || props.currentReport?.displayType === chartTypes.AREA,
            })}
            sx={{
                mt:2,
                width:1,
                display:'flex',
                justifyContent:"space-between",
                alignItems:'center',
                paddingX:4,
                pr:1,
                "@media screen and (max-width: 63.9735em)" :{
                    flexDirection:'column',
                    alignItems:'flex-start',
                    gap:0,
                },
                "@media screen and (max-width: 47.9735em)" :{
                    px:3,
                    "&.mobile-line": {
                        pr:0,
                    }
                },
                '@media screen and (max-width: 34.375em)': {
                    mt:0,
                    gap:3,
                },
            }}
        >
            <Box
                sx={{
                    display:"none",
                    position:'fixed',
                    top:"72px",
                    width:1,
                    left:"0px",
                    pl:2,
                    background:colors.bg["tertiary"],
                    zIndex:100,
                    pb:2,
                    "@media screen and (max-width: 47.9735em)" :{
                        display:"flex",
                        alignItems:'center',
                        zIndex:999,
                    },
                }}
            >
                <Box
                    sx={{
                        flex:1,
                        display:"flex",
                        alignItems:'center',
                    }}
                >
                    {edittingName?
                        <Box
                            className={cx(undefined, {
                                'is-error':props.nameError,
                            })}
                            sx={{
                                display:'flex',
                                alignItems:"center",
                                px:0.5,
                                height:32,
                                borderRadius:12,
                                minWidth:450,
                                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                "@media screen and (max-width: 768px)" : {
                                    minWidth:"unset",
                                    width:1,
                                },
                                "&.is-error" : {
                                    background:colors.red[100],
                                    borderColor:colors.red[400],
                                }
                            }}
                        >
                            <Box
                                className={cx(undefined, {
                                    'is-error':props.nameError,
                                })}
                                sx={{
                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                    height:24,
                                    borderRadius:4,
                                    px:2,
                                    lineHeight:"24px",
                                    "&.is-error": {
                                        background:colors.red[400],
                                    }
                                }}
                            >
                                <Text
                                    color={props.nameError && (theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['light'])}
                                >
                                    {t("name")}
                                </Text>
                            </Box>
                            <InputBase 
                                value={props.currentReport?.name}
                                onChange={(e) => {
                                    props.setNameError(false);
                                    props.setCurrentReport(prev => ({
                                        ...prev,
                                        name:e.target.value,
                                    }))
                                }}
                                name='report_name'
                                placeholder={t("report_name")}
                                sx={{
                                    flex:1,
                                    height:1,
                                    width:'auto',
                                    fontSize:14,
                                    px:2,
                                    // py:1,
                                    fontWeight:1000,
                                    fontFamily:"Roboto, sans-serif",
                                    color:colors.txt["primary"],
                                }}
                            />
                        </Box>
                    :
                        <Heading >
                            {props.currentReport?.name}
                        </Heading>
                    }
                    {!props.defaultNameEdit && (
                        <ButtonBase
                            onClick={() => {
                                setEdittingName(prev => !prev);
                            }}
                            sx={{
                                flexShrink:0,
                                ml:2,
                                height:32,
                                // width:32,
                                // background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[400],
                                borderRadius:4,
                                color:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt['secondary'],
                            }}
                        >
                            {edittingName ? <EditIcon/> : <EditOutlinedIcon />}
                        </ButtonBase>
                    )}
                </Box>
                <Box
                    sx={{
                        background:theme.palette.mode === 'dark' ? colors.grey[500] : colors.grey[400],
                        borderTopLeftRadius:16,
                        borderBottomLeftRadius:16,
                        display:"flex",
                        ml:2,
                        height:32,
                    }}
                >
                    <ButtonBase
                        onClick={()=> {
                            rhSettingsModal.setOpen(true);
                        }}
                        sx={{
                            px:2,
                            borderTopLeftRadius:16,
                            borderBottomLeftRadius:16,
                            color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"],
                        }}
                    >
                        <SettingsOutlined fontSize="small"/>
                    </ButtonBase>
                    <ButtonBase
                        onClick={()=> {
                            rhFilterModal.setOpen(true);
                        }}
                        sx={{
                            px:2,
                            borderTopLeftRadius:16,
                            borderBottomLeftRadius:16,
                            background:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                        }}
                    >
                        <FilterAltOutlinedIcon fontSize="small"/>
                    </ButtonBase>
                </Box>
                
            </Box>

            <Box
                sx={{
                    width:1,
                    "@media screen and (max-width: 63.9735em)" :{
                        mb:4,
                    },
                    "@media screen and (max-width: 47.9735em)" :{
                        mt:7,
                    },
                    '@media screen and (max-width: 34.375em)': {
                        mb:0,
                    },

                }}
            >
                <Box
                    sx={{
                        display:"flex",
                        width:1,
                        justifyContent:'space-between',
                        // alignItems:''
                        pr:3,
                        "@media screen and (max-width: 768px)" : {
                            display:'none',
                        }
                    }}
                >
                    <Box
                        sx={{
                            display:"flex",
                            alignItems:'center',
                            gap:1,
                            minWidth:400,
                            "@media screen and (max-width: 768px)" : {
                                display:'none'
                            }
                        }}
                    >
                        
                        {edittingName?
                            <Box
                                className={cx(undefined, {
                                    'is-error':props.nameError,
                                })}
                                sx={{
                                    display:'flex',
                                    alignItems:"center",
                                    px:0.5,
                                    height:40,
                                    borderRadius:12,
                                    minWidth:450,
                                    border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200]}`,
                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                    "@media screen and (max-width: 768px)" : {
                                        minWidth:"unset",
                                        width:1,
                                    },
                                    "&.is-error" : {
                                        background:colors.red[100],
                                        borderColor:colors.red[400],
                                    }
                                }}
                            >
                                <Box
                                    className={cx(undefined, {
                                        'is-error':props.nameError,
                                    })}
                                    sx={{
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                        height:32,
                                        borderRadius:4,
                                        px:2,
                                        lineHeight:"32px",

                                        "&.is-error": {
                                            background:colors.red[400],
                                        }
                                    }}
                                >
                                    <Text
                                        color={props.nameError && (theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['light'])}
                                    >
                                        {t("name")}
                                    </Text>
                                </Box>
                                <InputBase 
                                    value={props.currentReport?.name}
                                    onChange={(e) => {
                                        props.setNameError(false);
                                        props.setCurrentReport(prev => ({
                                            ...prev,
                                            name:e.target.value,
                                        }))
                                    }}
                                    name='report_name'
                                    placeholder={t("report_name")}
                                    sx={{
                                        flex:1,
                                        height:1,
                                        width:'auto',
                                        fontSize:21,
                                        px:2,
                                        // py:1,
                                        fontWeight:1000,
                                        fontFamily:"Roboto, sans-serif",
                                        color:colors.txt["primary"],
                                    }}
                                />
                            </Box>
                        :
                            <Heading>
                                {props.currentReport?.name}
                            </Heading>
                        }
                        {!props.defaultNameEdit && (
                            <ButtonBase
                                onClick={() => {
                                    setEdittingName(prev => !prev);
                                }}
                                sx={{
                                    height:40,
                                    width:40,
                                    // background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                    color:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt['secondary'],
                                    borderRadius:4,
                                }}
                            >
                                {edittingName ? <EditIcon/> : <EditOutlinedIcon />}
                            </ButtonBase>
                        )}

                        {/* <ButtonBase
                            onClick={handlePopoverClick}
                            sx={{
                                background:theme.palette.mode === "dark" ? colors.txt["secondary"] :colors.txt["primary"],
                                height:40,
                                width:40,
                                borderRadius:7,
                                display:'flex',
                                alignItems:'center',
                                justifyContent:'center',
                                color:theme.palette.mode==="dark"?colors.txt["primary"]:colors.txt['light'],
                            }}
                        >
                            <SettingsIcon/>
                        </ButtonBase> */}
                    </Box>

                    {!(props.currentReport?.category?.use && categorySettings?.defaultDateTime?.custom) ? (
                        <RhDateSelector
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                            sx={{width:650}}
                        />
                    ) : (
                        // We can just render the component out here if it is there
                        categorySettings?.defaultDateTime?.component && (
                            <categorySettings.defaultDateTime.component
                                setCurrentReport={props.setCurrentReport}
                                currentReport={props.currentReport}
                            />
                        )
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <RhSubtypeToggle
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                        />
                        <Box
                            className={cx(undefined,{
                                'is-category':props.currentReport?.category?.use
                            })}
                            sx={{
                                display:"flex",
                                justifyContent:"center",
                                gap:1,
                                "&.is-category": {
                                    height:40,
                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                                    p:0.5,
                                    borderRadius:5,
                                    display:"flex",
                                    gap:1.5,
                                    minWidth:400,
                                    // width:400,
                                }
                            }}
                        >
                            <ToggleButtonGroup
                                // color={props.currentReport?.category?.use ? "green" : "red"}
                                value={props.currentReport?.category?.use}
                                exclusive
                                onChange={(e,value) => {
                                    if(value === null) return;
                                    //for default date time we overwrite date: 
                                
                                    props.setCurrentReport(prev => {
                                        let nextReport = {
                                            ...prev,
                                            date: {
                                                ...prev.date,
                                                custom:false,
                                            },
                                            category: {
                                                ...prev.category,
                                                use: value,
                                            },
                                            ...(value && { displayType: chartTypes.BAR }),
                                        };

                                        if (value === true) {
                                            if (categorySettings?.defaultDateTime?.custom) {
                                                nextReport = {
                                                    ...nextReport,
                                                    date: {
                                                        ...categorySettings.defaultDateTime,
                                                    },
                                                };
                                            }
                                        }

                                        return nextReport;
                                    });
                                }}

                                aria-label="Active"

                                className={cx(`${props.currentReport?.category?.use}`, {
                                    'active': props.currentReport?.category?.use 
                                })}
                                sx={{
                                    width:1,
                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                    padding:0.5,
                                    borderRadius:5,
                                    transition:"250ms",
                                    "&.active" : {
                                        background: colors.grey[200],
                                        transition:"250ms",
                                        p:0,
                                    },
                                }}
                            >
                                <ToggleButton
                                    sx={{
                                        flex:1,
                                        padding: 0,
                                        paddingX: 2,
                                        border: 0,
                                        lineHeight: "32px",
                                        textTransform: "unset",
                                        borderRadius: "32px !important",
                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 

                                        background: theme.palette.mode === "dark" ? colors.grey[200] :colors.grey[200], 
                                        transition: "250ms",
                                        "&.Mui-selected": {
                                            background: theme.palette.mode === "dark" ? colors.grey[800] :colors.grey[100], 
                                            color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400], 
                                            fontWeight:"bold",
                                        },
                                        "&.Mui-selected:hover": {
                                            background:theme.palette.mode === "dark" ? colors.grey[600]: colors.grey[200],
                                        },
                                    }}
                                    value={false}
                                >
                                    {t('use_date')}
                                </ToggleButton>
                                <ToggleButton
                                    sx={{
                                        flex:1,
                                        padding: 0,
                                        paddingX: 2,
                                        border: 0,
                                        lineHeight: "32px",
                                        textTransform: "unset",
                                        borderRadius: "32px !important",
                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 

                                        background: theme.palette.mode === "dark" ? colors.grey[400] :colors.grey[0], 
                                        transition: "250ms",
                                        "&.Mui-selected": {
                                            background: theme.palette.mode === "dark" ? colors.grey[400] :colors.grey[0], 
                                            color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400], 
                                            fontWeight:"bold",
                                        },
                                        "&.Mui-selected:hover": {
                                            background:theme.palette.mode === "dark" ? colors.grey[600]: colors.grey[0],
                                        },
                                    }}
                                    value={true}
                                >
                                    {t('use_category')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {!props.currentReport?.category?.use && (
                                <ToggleButtonGroup
                                    value={props.currentReport?.displayType}
                                    exclusive
                                    onChange={(e,value) => {
                                        if(value != null){
                                            props.setCurrentReport(prev => ({
                                                ...prev,
                                                displayType:value,
                                            }));
                                        }
                                    }}

                                    aria-label="Active"

                                    className={cx(`${props.currentReport?.category?.use}`, {
                                        'active': !props.currentReport?.category?.use 
                                    })}
                                    sx={{
                                        width:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                        padding:0.5,
                                        borderRadius:5,
                                        transition:"250ms",
                                        "&.active" : {
                                            // background: colors.grey[200],
                                            transition:"250ms",
                                        },
                                    }}
                                >
                                    <ToggleButton
                                        sx={{
                                            flex:1,
                                            padding: 0,
                                            paddingX: 2,
                                            border: 0,
                                            lineHeight: "32px",
                                            textTransform: "unset",
                                            borderRadius: "32px !important",
                                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 

                                            background: theme.palette.mode === "dark" ? colors.grey[400] :colors.grey[0], 
                                            transition: "250ms",
                                            "&.Mui-selected": {
                                                background: theme.palette.mode === "dark" ? colors.grey[800] :colors.grey[100], 
                                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400], 
                                                fontWeight:"bold",
                                            },
                                            "&.Mui-selected:hover": {
                                                background:theme.palette.mode === "dark" ? colors.grey[600]: colors.grey[200],
                                            },
                                        }}
                                        value={chartTypes.BAR}
                                    >
                                        {t('chart_bar')}
                                    </ToggleButton>
                                    <ToggleButton
                                        sx={{
                                            flex:1,
                                            padding: 0,
                                            paddingX: 2,
                                            border: 0,
                                            lineHeight: "32px",
                                            textTransform: "unset",
                                            borderRadius: "32px !important",
                                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 

                                            background: theme.palette.mode === "dark" ? colors.grey[400] :colors.grey[0], 
                                            transition: "250ms",
                                            "&.Mui-selected": {
                                                background: theme.palette.mode === "dark" ? colors.grey[800] :colors.grey[100], 
                                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400], 
                                                fontWeight:"bold",
                                            },
                                            "&.Mui-selected:hover": {
                                                background:theme.palette.mode === "dark" ? colors.grey[600]: colors.grey[200],
                                            },
                                        }}
                                        value={chartTypes.LINE}
                                    >
                                        {t('chart_line')}
                                    </ToggleButton>
                                    <ToggleButton
                                        sx={{
                                            flex:1,
                                            padding: 0,
                                            paddingX: 2,
                                            border: 0,
                                            lineHeight: "32px",
                                            textTransform: "unset",
                                            borderRadius: "32px !important",
                                            color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 

                                            background: theme.palette.mode === "dark" ? colors.grey[400] :colors.grey[0], 
                                            transition: "250ms",
                                            "&.Mui-selected": {
                                                background: theme.palette.mode === "dark" ? colors.grey[800] :colors.grey[100], 
                                                color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.grey[400], 
                                                fontWeight:"bold",
                                            },
                                            "&.Mui-selected:hover": {
                                                background:theme.palette.mode === "dark" ? colors.grey[600]: colors.grey[200],
                                            },
                                        }}
                                        value={chartTypes.AREA}
                                    >
                                        {t('chart_area')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}

                            {props.currentReport?.category?.use && (
                                <ButtonBase
                                    onClick={handlePopoverClick}
                                    sx={{
                                        background:
                                            theme.palette.mode === "dark"
                                            ? colors.grey[200]
                                            : colors.grey[400],
                                        height: 32,
                                        borderRadius: 4,
                                        px: 1.5,
                                        pr:props.currentReport?.category.key ? 0.5 : 1.5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        whiteSpace:"nowrap",
                                        gap: 1.5,
                                        color:
                                            theme.palette.mode === "dark"
                                            ? colors.txt.primary
                                            : colors.txt.light,
                                        fontWeight: 500,
                                        fontSize:  14,
                                        fontFamily: "Roboto,sans-serif",
                                    }}
                                >
                                    {props.currentReport?.category.key
                                        ? t(filtersRegistry.filters[props.currentReport?.category.key].name)
                                        : t("no_key_selected")}

                                    {props.currentReport?.category.key && (
                                            <Box
                                            sx={{
                                                px:1,
                                                height:24,
                                                borderRadius:4,
                                                display:"flex",
                                                alignItems:'center',
                                                cursor:"pointer",
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                "&:hover .rp-value-container" : {
                                                    pr:4,
                                                },
                                                "&:hover .rp-value-container .rp-filter-icon" : {
                                                    zIndex:1,
                                                    opacity:1,
                                                        transition:"125ms",
                                                },
                                            }}
                                        >
                                            {categorySelectionCount > 0 ?
                                                <>
                                                    <Text noBreak bold>{categorySelectionCount}</Text>
                                                    <Text noBreak light sx={{ml:0.5,}}>
                                                        {t("selected")}
                                                    </Text>
                                                </>
                                            :
                                                <Text noBreak>{t("no_selection")}</Text>
                                            }
                                        </Box>
                                    )}
                                </ButtonBase>
                            )}
                            {/* <Shbutton
                                onClick={handleRegenBi}
                            >
                                {t("regen")}
                            </Shbutton> */}

                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width:1,
                        // background:'#fff',
                        // height:32,
                        display:"flex",
                        mt:1,
                        "@media screen and (max-width: 768px)" : {
                            display:'none',
                        }
                    }}
                >
                    <RhFilter

                        currentReport={props.currentReport}
                        setCurrentReport={props.setCurrentReport}
                    />
                    {showCompareBar &&
                        <RhRollingCompare
                            currentReport={props.currentReport}
                            setCurrentReport={props.setCurrentReport}
                            rolling={rolling}
                        />
                    }
                </Box>

                {/* Rolling comparison list — add/remove the periods to overlay against the current one */}
                

                {/* Important to note that the legend exists within the chart */}
                {/* Chart container with skeleton placeholder and fade-in */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: 300,
                        width: 1,
                    }}
                >
                    {/* Skeleton loader - shown before first data load */}
                    {showSkeleton && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                borderRadius: 6,
                                mt: 2,
                                width: "calc(100% - 24px)",
                                height: 260,
                                mr: 3,
                                mb: 4,
                                bgcolor: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress
                                size={40}
                                thickness={4}
                                sx={{
                                    color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                }}
                            />
                        </Box>
                    )}

                    {/* Refetch spinner - shown in top-left when fetching new data after initial load */}
                    {chartRendered && props.fetchFetching && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 16,
                                left: 24,
                                zIndex: 10,
                            }}
                        >
                            <CircularProgress
                                size={24}
                                thickness={4}
                                sx={{
                                    color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                }}
                            />
                        </Box>
                    )}

                    {/* Chart content - render hidden until ready, then fade in */}
                    {/* Apply blur effect when refetching data after chart is already rendered */}
                    <Box
                        sx={{
                            width: 1,
                            // Hide chart visually while it renders, but keep it in DOM
                            visibility: chartRendered ? 'visible' : 'hidden',
                            opacity: chartRendered ? 1 : 0,
                            transition: 'opacity 300ms ease-in-out, filter 200ms ease-in-out',
                            filter: (chartRendered && props.fetchFetching) ? 'blur(3px)' : 'none',
                        }}
                    >
                            {props.currentReport?.displayType === chartTypes.BAR ? (
                                <ErrorBoundary
                                    FallbackComponent={() => <div>Barchart heeft zich niet kunnen inladen</div>}
                                    onError={(error, info) => console.error(error, info)}
                                >
                                    <RHBarChart
                                        chartSettings={filtersRegistry.chartSettings}
                                        isLoading={false}
                                        isError={props.isError}
                                        chartOption={chartOption}
                                        currentReport={props.currentReport}
                                        values={chartValues}
                                        setCurrentReport={props.setCurrentReport}
                                        onRetry={props.refetch}
                                        categories={chartCategories}
                                        staticColors={theme.palette.mode === "dark" ? rhChartColorsDark : rhChartColorsLight}
                                    />
                                </ErrorBoundary>
                            ) : props.currentReport?.displayType === chartTypes.AREA ? (
                                <ErrorBoundary
                                    FallbackComponent={() => <div>Areachart heeft zich niet kunnen inladen</div>}
                                    onError={(error, info) => console.error(error, info)}
                                >
                                    <AreaChartContentReports
                                        chartSettings={filtersRegistry.chartSettings}
                                        onRetry={props.refetch}
                                        color="brown"
                                        staticColors={theme.palette.mode === "dark" ? rhChartColorsDark : rhChartColorsLight}
                                        values={chartValues}
                                        categories={chartCategories}
                                        isLoading={false}
                                        isError={props.isError}
                                    />
                                </ErrorBoundary>
                            ) : props.currentReport?.displayType === chartTypes.LINE ? (
                                <ErrorBoundary
                                    FallbackComponent={() => <div>Linehcart heeft zich niet kunnen inladen</div>}
                                    onError={(error, info) => console.error(error, info)}
                                >
                                    <LineChartContentReports
                                        chartSettings={filtersRegistry.chartSettings}
                                        onRetry={props.refetch}
                                        color="brown"
                                        staticColors={theme.palette.mode === "dark" ? rhChartColorsDark : rhChartColorsLight}
                                        values={chartValues}
                                        categories={chartCategories}
                                        isLoading={false}
                                        isError={props.isError}
                                    />
                                </ErrorBoundary>
                            ) : (
                                <Skeleton
                                    sx={{
                                        borderRadius: 6,
                                        mt: 2,
                                        width: "calc(100% - 24px)",
                                        height: 260,
                                        mr: 3,
                                        mb: 4,
                                        bgcolor: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                        transform: "none",
                                    }}
                                    animation="wave"
                                />
                            )}
                    </Box>
                </Box>
            </Box>
            <ReportDetailsPageDetails
                chartOption={chartOption}
                currentReport={props.currentReport}
                filtersRegistry={filtersRegistry}
                setCurrentReport={props.setCurrentReport}
                id={popoverId}
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                stopPropagation={true}
            />
            <MRhFilter
                onOpen={() => rhFilterModal.setOpen(true)} 
                onClose={() => rhFilterModal.setOpen(false)} 
                open={rhFilterModal.open} 
                handleClose={handleRhFilterClose} 

                currentReport={props.currentReport}
                setCurrentReport={props.setCurrentReport}
            />

            <MRhSettings
                onOpen={() => rhSettingsModal.setOpen(true)} 
                onClose={() => rhSettingsModal.setOpen(false)} 
                open={rhSettingsModal.open} 
                handleClose={handleRhSettingsClose} 

                currentReport={props.currentReport}
                setCurrentReport={props.setCurrentReport}
                filtersRegistry={filtersRegistry}
                chartOption={chartOption}
                categorySelectionCount={categorySelectionCount}
            />
        </Box>
    )
}